import { Link } from "react-router-dom";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { ArrowLeft } from "lucide-react";

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-cream">
      <SiteHeader />
      <main id="main-content" className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <Link to="/" className="inline-flex items-center text-sm text-inkMute hover:text-chocolate" data-testid="back-home">
          <ArrowLeft size={14} className="mr-1.5" /> Back home
        </Link>
        <h1 className="page-title mt-8" data-testid="privacy-heading">Privacy Policy</h1>
        <p className="text-sm text-inkMute mt-3">Last updated: February 2026</p>
        <aside className="policy-notice mt-8" data-testid="privacy-review-notice">
          <strong>This policy is being updated.</strong>
          <p className="mt-2">Keepsake Books is our public brand name. The policy below still refers to Keepsake Stories while we update it.</p>
          <details className="mt-3"><summary className="cursor-pointer min-h-[44px]" data-testid="privacy-review-details-toggle">What is still being confirmed</summary><ul className="list-disc pl-5 space-y-2" data-testid="privacy-review-details">
            <li>The automatic archiving and deletion schedules described below are not yet in place. Removing a photo from your book does not delete the stored file.</li>
            <li>Photos are retrieved using a file link. Customer-only access and the storage commitments below have not yet been independently verified.</li>
            <li>Illustration requests to our AI provider can include character names, mood and your illustration notes, as well as scene descriptions and style. This is more than some statements below suggest.</li>
            <li>Order emails, provider retention terms, fulfilment responsibilities and our legal entity name are still being confirmed.</li>
            <li>Your browser stores an anonymous identifier and draft recovery data. Please share only content you have permission to use.</li>
          </ul></details>
        </aside>

        <div className="mt-10 prose prose-stone max-w-none space-y-8 text-ink leading-relaxed">

          <section>
            <h2 className="font-serif text-2xl text-ink mb-3">1. Who we are</h2>
            <p className="text-inkMute">
              Keepsake Stories ("we", "us", "our") is a personalized storybook creation service operated in India.
              We turn the memories, photos, and feelings you share into beautifully written, printed keepsake books.
              This Privacy Policy explains what data we collect, why we collect it, how we use it, and the choices you have.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl text-ink mb-3">2. Information you give us</h2>
            <ul className="list-disc pl-5 space-y-1.5 text-inkMute">
              <li><strong className="text-ink">Storybook content:</strong> the people, relationships, memories, dialogue, dedications, and any other answers you provide in the questionnaire.</li>
              <li><strong className="text-ink">Photos:</strong> images you upload to be printed in the book or used as character references for illustrations.</li>
              <li><strong className="text-ink">Order details:</strong> name, email, phone, shipping address, recipient name, gift notes, needed to print and deliver your book.</li>
              <li><strong className="text-ink">Payment metadata:</strong> when you place an order via a payment provider, we receive transaction status, never your card number.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-serif text-2xl text-ink mb-3">3. How we use your information</h2>
            <ul className="list-disc pl-5 space-y-1.5 text-inkMute">
              <li>To generate the personalized written draft of your book using AI (Claude Sonnet 4.5 by Anthropic).</li>
              <li>To generate full-page illustrations for your book using image AI (OpenAI gpt-image-1). Only the chapter scene description and your selected illustration style are sent.</li>
              <li>To print and ship the physical book to the address you provide.</li>
              <li>To send you order status updates (draft writing, in production, shipped, delivered).</li>
              <li>To improve our writing prompts and editor tooling, only on aggregated, de-identified signals.</li>
            </ul>
            <p className="text-inkMute mt-3">
              <strong className="text-ink">We do not sell your memories, photos, or personal details to anyone.</strong> Ever.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl text-ink mb-3">4. Photos and memories</h2>
            <p className="text-inkMute">
              The photos and memories you upload are deeply personal. We treat them that way:
            </p>
            <ul className="list-disc pl-5 space-y-1.5 text-inkMute mt-3">
              <li>Photos are stored in private, access-controlled object storage. They are never publicly indexed.</li>
              <li>Photos are only retrieved by you (via your project) and by our editorial team for the sole purpose of producing your book.</li>
              <li>If you delete a photo from your project, it is soft-deleted from storage immediately and purged from our backups within 30 days.</li>
              <li>We do not use your photos to train any AI model. Photos passed to image-generation services are sent only for the duration of a single render call and are not retained by those providers per their API policies.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-serif text-2xl text-ink mb-3">5. Third-party processors</h2>
            <p className="text-inkMute mb-3">We use a small set of trusted vendors to deliver the service:</p>
            <ul className="list-disc pl-5 space-y-1.5 text-inkMute">
              <li><strong className="text-ink">Anthropic (Claude)</strong>: for writing the book draft. Receives the text content of your questionnaire only.</li>
              <li><strong className="text-ink">OpenAI (gpt-image-1)</strong>: for generating illustrations. Receives the chapter scene description and your chosen illustration style only.</li>
              <li><strong className="text-ink">Object storage provider</strong>: for storing your uploaded photos in encrypted-at-rest, access-controlled buckets.</li>
              <li><strong className="text-ink">Print &amp; logistics partners</strong>: receive only the final printable book file and the shipping address.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-serif text-2xl text-ink mb-3">6. Cookies &amp; local storage</h2>
            <p className="text-inkMute">
              We use browser <code>localStorage</code> to remember your in-progress project (so you don't lose progress mid-questionnaire) and to keep an anonymous identifier so we can show you your own projects. We do not use third-party advertising cookies.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl text-ink mb-3">7. How long we keep your data</h2>
            <ul className="list-disc pl-5 space-y-1.5 text-inkMute">
              <li>Draft projects (not yet ordered): 12 months from last edit, then archived.</li>
              <li>Ordered projects, generated book drafts, and order records: 7 years (legal and reprint requirements).</li>
              <li>Uploaded photos: until the order is fulfilled + 90 days, after which they are purged unless you've requested reprint rights.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-serif text-2xl text-ink mb-3">8. Your rights</h2>
            <p className="text-inkMute">You can, at any time, request to:</p>
            <ul className="list-disc pl-5 space-y-1.5 text-inkMute mt-2">
              <li>See a copy of the data we hold about you.</li>
              <li>Correct or update any information.</li>
              <li>Delete a project, an order, or all of your data (subject to the legal retention requirements above).</li>
              <li>Withdraw consent to use of any specific photo or memory, even after the book has been generated.</li>
            </ul>
            <p className="text-inkMute mt-3">
              Email us at <a href="mailto:hello@keepsakestories.in" className="text-chocolate underline" data-testid="privacy-rights-email">hello@keepsakestories.in</a> and we'll respond within 7 working days.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl text-ink mb-3">9. Children</h2>
            <p className="text-inkMute">
              Keepsake Stories is intended for users 18 years and older. Books may be <em>about</em> children (or written for children as a gift), but the account holder placing the order must be an adult.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl text-ink mb-3">10. Updates to this policy</h2>
            <p className="text-inkMute">
              We may update this Privacy Policy when we add features or change vendors. We'll always post the latest version here with the updated date. Material changes will be communicated to the email on your most recent order.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl text-ink mb-3">11. Contact</h2>
            <p className="text-inkMute">
              Questions, requests, or concerns: <a href="mailto:hello@keepsakestories.in" className="text-chocolate underline" data-testid="privacy-contact-email">hello@keepsakestories.in</a>.
            </p>
          </section>

        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
